<script setup lang="ts">
import {
  computed,
  onMounted,
  onUnmounted,
  ref,
  useTemplateRef,
  watch,
  type Component,
} from "vue";
import type { FormatCommand } from "../editor/port";
import { EDITOR_FONTS, fontStackFor, type EditorFontId } from "../editor/fonts";
import {
  useAnchoredFloating,
  type AnchorRect,
} from "../composables/useAnchoredFloating";
import { just, nothing, toMaybe, type Maybe } from "../lib/maybe";
import ZwMenu from "./ui/ZwMenu.vue";
import ZwMenuItem from "./ui/ZwMenuItem.vue";
import ZwMenuSeparator from "./ui/ZwMenuSeparator.vue";
import {
  IconAlignCenter,
  IconAlignLeft,
  IconAlignRight,
  IconBold,
  IconBulletList,
  IconComment,
  IconHeading1,
  IconHeading2,
  IconHeading3,
  IconHighlight,
  IconImage,
  IconIndent,
  IconItalic,
  IconLink,
  IconNumberedList,
  IconOutdent,
  IconQuote,
  IconSpacer,
  IconStrikethrough,
  IconUnderline,
} from "./ribbonIcons";
import { modalDialogOpen } from "../lib/modalDialog";

const props = withDefaults(
  defineProps<{
    visible: boolean;
    /** Viewport-relative box to sit above (the selection, or the mobile dock). */
    anchor: Maybe<AnchorRect>;
    modLabel: string;
    altLabel: string;
    bodyFontId?: EditorFontId;
    headingFontId?: EditorFontId;
    showFonts?: boolean;
    /** Selection-based bubble; skip hover pin (mobile shell). */
    touchMode?: boolean;
    /** Reading mode: only Add comment (no formatting controls). */
    commentOnly?: boolean;
  }>(),
  {
    showFonts: false,
    touchMode: false,
    commentOnly: false,
  },
);

const emit = defineEmits<{
  command: [command: FormatCommand];
  "update:bodyFontId": [fontId: EditorFontId];
  "update:headingFontId": [fontId: EditorFontId];
  /** Pointer entered the bubble — parent should keep it visible. */
  pin: [pinned: boolean];
  /** Escape — parent collapses the selection so the dock stays hidden. */
  dismiss: [];
}>();

type Item = {
  command: FormatCommand;
  icon: Component;
  label: string;
  shortcut?: string;
};

const primary = computed<Item[]>(() => {
  const comment: Item = {
    command: "addComment",
    icon: IconComment,
    label: "Add comment",
    shortcut: `${props.modLabel}⇧M`,
  };
  if (props.commentOnly) return [comment];
  return [
    {
      command: "bold",
      icon: IconBold,
      label: "Bold",
      shortcut: `${props.modLabel}B`,
    },
    {
      command: "italic",
      icon: IconItalic,
      label: "Italic",
      shortcut: `${props.modLabel}I`,
    },
    comment,
  ];
});

const more = computed<Item[]>(() => {
  if (props.commentOnly) return [];
  return [
    {
      command: "underline",
      icon: IconUnderline,
      label: "Underline",
      shortcut: `${props.modLabel}U`,
    },
    {
      command: "strikethrough",
      icon: IconStrikethrough,
      label: "Strikethrough",
      shortcut: `${props.modLabel}⇧X`,
    },
    {
      command: "highlight",
      icon: IconHighlight,
      label: "Highlight",
      shortcut: `${props.modLabel}⇧H`,
    },
    {
      command: "bulletList",
      icon: IconBulletList,
      label: "Bullet list",
      shortcut: `${props.modLabel}⇧8`,
    },
    {
      command: "numberedList",
      icon: IconNumberedList,
      label: "Numbered list",
      shortcut: `${props.modLabel}⇧7`,
    },
    {
      command: "quote",
      icon: IconQuote,
      label: "Quote",
      shortcut: `${props.modLabel}⇧.`,
    },
    {
      command: "outdent",
      icon: IconOutdent,
      label: "Outdent",
      shortcut: `${props.modLabel}[`,
    },
    {
      command: "indent",
      icon: IconIndent,
      label: "Indent",
      shortcut: `${props.modLabel}]`,
    },
    {
      command: "alignLeft",
      icon: IconAlignLeft,
      label: "Align left",
    },
    {
      command: "alignCenter",
      icon: IconAlignCenter,
      label: "Align center",
    },
    {
      command: "alignRight",
      icon: IconAlignRight,
      label: "Align right",
    },
    {
      command: "link",
      icon: IconLink,
      label: "Link",
      shortcut: `${props.modLabel}K`,
    },
    {
      command: "image",
      icon: IconImage,
      label: "Image",
      shortcut: `${props.modLabel}⇧K`,
    },
    {
      command: "spacer",
      icon: IconSpacer,
      label: "Vertical space",
    },
    {
      command: "heading1",
      icon: IconHeading1,
      label: "Heading 1",
      shortcut: `${props.modLabel}${props.altLabel}1`,
    },
    {
      command: "heading2",
      icon: IconHeading2,
      label: "Heading 2",
      shortcut: `${props.modLabel}${props.altLabel}2`,
    },
    {
      command: "heading3",
      icon: IconHeading3,
      label: "Heading 3",
      shortcut: `${props.modLabel}${props.altLabel}3`,
    },
  ];
});

const moreOpen = ref(false);
const bubble = useTemplateRef<HTMLElement>("bubble");

// Teleported to <body> because .zw-editor-shell is overflow:hidden and would
// otherwise clip the bubble near the edges. Prefers sitting above the anchor and
// flips below it when the selection is on the first visible line.
const anchor = computed(() => props.anchor);
const open = computed(() => props.visible);
const panel = computed(() => toMaybe(bubble.value));
const { floatingStyle } = useAnchoredFloating(anchor, panel, open, {
  placement: "top",
  gap: 8,
});

watch(
  () => props.visible,
  (visible) => {
    if (visible === false) moreOpen.value = false;
  },
);

function itemTitle(item: Item): string {
  return (item.shortcut ?? "").length > 0
    ? `${item.label} (${item.shortcut})`
    : item.label;
}

function onAction(command: FormatCommand): void {
  moreOpen.value = false;
  emit("command", command);
}

function onBodyFont(event: Event): void {
  const value = (event.target as HTMLSelectElement).value as EditorFontId;
  emit("update:bodyFontId", value);
}

function onHeadingFont(event: Event): void {
  const value = (event.target as HTMLSelectElement).value as EditorFontId;
  emit("update:headingFontId", value);
}

function onKeydown(event: KeyboardEvent): void {
  if (event.key !== "Escape" || props.visible === false) return;
  // Let the overflow menu consume Escape first.
  if (moreOpen.value === true) return;
  // Modals (settings, etc.) own Escape while open.
  if (modalDialogOpen() === true) return;
  event.preventDefault();
  moreOpen.value = false;
  emit("dismiss");
}

onMounted(() => {
  window.addEventListener("keydown", onKeydown);
});

onUnmounted(() => {
  window.removeEventListener("keydown", onKeydown);
});
</script>

<template>
  <Teleport to="body">
    <div
      v-show="visible"
      ref="bubble"
      class="format-bubble"
      :class="{
        'format-bubble--touch': touchMode,
        'format-bubble--comment': commentOnly,
      }"
      role="toolbar"
      :aria-label="commentOnly ? 'Comments' : 'Formatting'"
      :style="floatingStyle"
      @pointerenter="!touchMode && emit('pin', true)"
      @pointerleave="!touchMode && emit('pin', false)"
    >
      <button
        v-if="commentOnly"
        type="button"
        class="format-bubble-comment"
        aria-label="Add comment"
        @mousedown.prevent="onAction('addComment')"
      >
        <IconComment aria-hidden="true" />
        <span>Comment</span>
      </button>

      <template v-else>
        <button
          v-for="item in primary"
          :key="item.command"
          type="button"
          class="zw-ribbon-btn format-bubble-btn"
          :title="itemTitle(item)"
          :aria-label="itemTitle(item)"
          @mousedown.prevent="onAction(item.command)"
        >
          <component :is="item.icon" class="size-4" aria-hidden="true" />
        </button>

        <div class="format-bubble-divider" aria-hidden="true" />

        <ZwMenu
          v-model:open="moreOpen"
          align="end"
          activate-on="mousedown"
          :menu-label="just('More formatting')"
        >
          <template #trigger="{ open: menuOpen, toggle, attrs }">
            <button
              type="button"
              class="zw-ribbon-btn format-bubble-btn"
              title="More formatting"
              aria-label="More formatting"
              v-bind="attrs"
              :aria-expanded="menuOpen"
              @mousedown.prevent="toggle"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="1.5"
                class="size-4"
                aria-hidden="true"
              >
                <path
                  stroke-linecap="round"
                  d="M6 12h.01M12 12h.01M18 12h.01"
                />
              </svg>
            </button>
          </template>

          <ZwMenuItem
            v-for="item in more"
            :key="item.command"
            :label="item.label"
            :shortcut="toMaybe(item.shortcut)"
            :icon="item.icon"
            :title="nothing"
            @select="onAction(item.command)"
          />

          <template
            v-if="
              showFonts &&
              typeof bodyFontId === 'string' &&
              typeof headingFontId === 'string'
            "
          >
            <ZwMenuSeparator />
            <label class="format-bubble-font">
              <span>Body</span>
              <select
                class="editor-font-select"
                :value="bodyFontId"
                :style="{ fontFamily: fontStackFor(bodyFontId) }"
                @mousedown.stop
                @change="onBodyFont"
              >
                <option
                  v-for="font in EDITOR_FONTS"
                  :key="font.id"
                  :value="font.id"
                  :style="{ fontFamily: font.stack }"
                >
                  {{ font.label }}
                </option>
              </select>
            </label>
            <label class="format-bubble-font">
              <span>Headings</span>
              <select
                class="editor-font-select"
                :value="headingFontId"
                :style="{ fontFamily: fontStackFor(headingFontId) }"
                @mousedown.stop
                @change="onHeadingFont"
              >
                <option
                  v-for="font in EDITOR_FONTS"
                  :key="font.id"
                  :value="font.id"
                  :style="{ fontFamily: font.stack }"
                >
                  {{ font.label }}
                </option>
              </select>
            </label>
          </template>
        </ZwMenu>
      </template>
    </div>
  </Teleport>
</template>

<style scoped>
/* position/left/top are set inline by useAnchoredFloating — it flips the bubble
   below the selection when there's no room above and slides it along the x axis
   so it can't hang off a narrow window. No transform offsets here: they would
   move the bubble out from under the box Floating UI measured. */
.format-bubble {
  z-index: 90;
  display: flex;
  align-items: center;
  gap: 0.15rem;
  padding: 0.25rem;
  border: 1px solid color-mix(in oklch, var(--line) 90%, transparent);
  border-radius: 0.45rem;
  background: color-mix(in oklch, var(--bg-raised) 94%, transparent);
  font-family: var(--font-ui);
  pointer-events: auto;
}

.format-bubble--comment {
  width: max-content;
  max-width: none;
  padding: 0.2rem;
}

.format-bubble-comment {
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  gap: 0.4rem;
  margin: 0;
  padding: 0.4rem 0.7rem;
  border: 0;
  border-radius: 0.35rem;
  background: transparent;
  color: var(--text-strong);
  font-family: var(--font-ui);
  font-size: 0.8125rem;
  font-weight: 500;
  line-height: 1;
  white-space: nowrap;
  cursor: pointer;
}

.format-bubble-comment:hover {
  background: var(--hover-fill);
}

.format-bubble-comment:focus-visible {
  outline: 2px solid var(--color-zw-amber);
  outline-offset: 1px;
}

/* Icons are functional components — scoped attrs don't land on the svg. */
.format-bubble-comment :deep(svg) {
  width: 1rem;
  height: 1rem;
  flex-shrink: 0;
  color: var(--text-muted);
}

.format-bubble-comment:hover :deep(svg) {
  color: var(--text-strong);
}

.format-bubble-btn {
  display: inline-flex;
  width: 1.75rem;
  height: 1.75rem;
  align-items: center;
  justify-content: center;
  border-radius: 0.3rem;
  flex-shrink: 0;
}

.format-bubble--touch .format-bubble-btn {
  width: 2.35rem;
  height: 2.35rem;
}

.format-bubble--touch .format-bubble-comment {
  min-height: 2.35rem;
  padding-inline: 0.85rem;
  font-size: 0.875rem;
}

.format-bubble--touch .format-bubble-comment :deep(svg) {
  width: 1.15rem;
  height: 1.15rem;
}

.format-bubble--touch {
  padding: 0.35rem;
  gap: 0.2rem;
}

.format-bubble-divider {
  width: 1px;
  height: 0.9rem;
  margin: 0 0.1rem;
  background: var(--line);
  flex-shrink: 0;
}

.format-bubble-font {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  padding: 0.35rem 0.45rem;
  font-size: 0.65rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--text-muted);
}

.format-bubble-font select {
  text-transform: none;
  letter-spacing: 0;
  font-size: 0.85rem;
  padding: 0.3rem 0.4rem;
  border: 1px solid var(--line);
  border-radius: 0.25rem;
  background: var(--bg);
  color: var(--text-strong);
}
</style>
